/**
 * Local PII scrubbing for browser Sentry events.
 * Mirrors API scrubbing — filter before anything leaves the machine.
 */

const REDACTED = '[Redacted]';

const SENSITIVE_KEY =
  /pass(word|code)?|secret|token|authorization|cookie|email|phone|mobile|fax|address|street|city|zip|postal|first_?name|last_?name|full_?name|customer_?name|imei|serial|ssn|dob|birth|card|cvv|cvc|pan|last_?4|last4|last_?four|expir|amount|total|balance|payment|transaction|auth|jwt|bearer|signature|pin/i;

const SENSITIVE_PATH =
  /\/api\/v1\/(customers|tickets|payments|sales|migration|portal|auth|waivers|messaging|inquiries|estimates|subscriptions|cash-sessions|public)/i;

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const PHONE_RE = /(?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)\d{3}[-.\s]?\d{4}\b/g;
const IMEI_RE = /\b\d{15}\b/g;
const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const CARD_RE = /\b(?:\d[ -]*?){13,19}\b/g;
const MONEY_RE = /\$\s?\d+(?:,\d{3})*(?:\.\d{2})?/g;

function scrubString(value: string): string {
  return value
    .replace(JWT_RE, REDACTED)
    .replace(EMAIL_RE, REDACTED)
    .replace(PHONE_RE, REDACTED)
    .replace(IMEI_RE, REDACTED)
    .replace(CARD_RE, REDACTED)
    .replace(MONEY_RE, REDACTED);
}

function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > 8) return REDACTED;
  if (value == null) return value;
  if (typeof value === 'string') return scrubString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_KEY.test(k)) {
        out[k] = REDACTED;
        continue;
      }
      if (
        k === 'id' ||
        k === 'userId' ||
        k === 'tenantId' ||
        k === 'storeId' ||
        k === 'ticketId' ||
        k === 'customerId' ||
        k.endsWith('Id')
      ) {
        out[k] = typeof v === 'string' || typeof v === 'number' ? v : scrubValue(v, depth + 1);
        continue;
      }
      out[k] = scrubValue(v, depth + 1);
    }
    return out;
  }
  return value;
}

function isSensitiveRoute(urlPath: string | undefined): boolean {
  if (!urlPath) return false;
  const pathOnly = urlPath.split('?')[0] ?? urlPath;
  return SENSITIVE_PATH.test(pathOnly);
}

export type ScrubbableEvent = {
  message?: string;
  user?: {
    id?: string;
    email?: string;
    username?: string;
    ip_address?: string;
    [key: string]: unknown;
  };
  request?: {
    url?: string;
    method?: string;
    headers?: Record<string, string>;
    data?: unknown;
    query_string?: unknown;
    cookies?: unknown;
    [key: string]: unknown;
  };
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  breadcrumbs?: Array<{
    message?: string;
    data?: Record<string, unknown>;
    [key: string]: unknown;
  }>;
  exception?: {
    values?: Array<{
      value?: string;
      [key: string]: unknown;
    }>;
  };
  tags?: Record<string, string>;
  [key: string]: unknown;
};

export function scrubSentryEvent(event: ScrubbableEvent): ScrubbableEvent | null {
  if (event.message) event.message = scrubString(event.message);

  if (event.user) {
    const userId = event.user.id;
    event.user = userId ? { id: String(userId) } : {};
  }

  if (event.request) {
    const url = event.request.url;
    const method = event.request.method;
    const pathOnly = typeof url === 'string' ? (url.split('?')[0] ?? url) : undefined;
    const headers = event.request.headers;
    const safeHeaders: Record<string, string> = {};
    if (headers && typeof headers === 'object') {
      for (const [k, v] of Object.entries(headers)) {
        if (/authorization|cookie|x-api-key|x-auth|token|set-cookie/i.test(k)) {
          safeHeaders[k] = REDACTED;
        } else if (typeof v === 'string') {
          safeHeaders[k] = scrubString(v);
        }
      }
    }
    const nextReq: NonNullable<ScrubbableEvent['request']> = {
      headers: safeHeaders,
      data: isSensitiveRoute(pathOnly) ? REDACTED : scrubValue(event.request.data),
    };
    if (pathOnly !== undefined) nextReq.url = pathOnly;
    if (method !== undefined) nextReq.method = method;
    event.request = nextReq;
  }

  if (event.extra) event.extra = scrubValue(event.extra) as Record<string, unknown>;
  if (event.contexts) event.contexts = scrubValue(event.contexts) as Record<string, unknown>;

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => {
      const next: Record<string, unknown> = { ...b };
      if (b.message !== undefined) next.message = scrubString(b.message);
      if (b.data !== undefined) next.data = scrubValue(b.data);
      return next as (typeof event.breadcrumbs)[number];
    });
  }

  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((v) => {
      const next: Record<string, unknown> = { ...v };
      if (v.value !== undefined) next.value = scrubString(v.value);
      return next as (typeof event.exception.values)[number];
    });
  }

  if (event.tags) {
    const allowed = new Set([
      'tenant_id',
      'store_id',
      'status_code',
      'route',
      'method',
      'transaction',
    ]);
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(event.tags)) {
      if (allowed.has(k) || k.startsWith('feature_')) {
        next[k] = scrubString(String(v));
      }
    }
    event.tags = next;
  }

  return event;
}
